import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import InlineBetProgress from 'components/InlinePlacement/components/InlineBetProgress';
import InlinePlacedBet from 'components/InlinePlacement/components/InlinePlacedBet';
import InlineSelectedBet from 'components/InlinePlacement/components/InlineSelectedBet';
import InlineUnavailableBet from 'components/InlinePlacement/components/InlineUnavailableBet';
import { MarketStatuses, PageBlocks } from 'constants/app';
import { BetsStatusesTypes } from 'constants/app';
import { InlinePlacementActions } from 'constants/inlinePlacement';
import { BettingTypes } from 'constants/markets';
import { BetSides } from 'constants/myBets';
import { getIsSportLayOddsDisabled, getMarketOddsBackOnly } from 'redux/modules/appConfigs/selectors';
import { getCurrentBetByOfferId } from 'redux/modules/currentBets/selectors';
import { removeInlineSelectedBet, updateInlineSelectedBet } from 'redux/modules/inlinePlacement';
import { TInlineSelectedBet } from 'redux/modules/inlinePlacement/type';
import { getStatusByMarketPricesId } from 'redux/modules/marketsPrices/selectors';
import { getMarketDataById, getPlacedSelectedBetErrorMessage } from 'redux/modules/placement/selectors';
import { PageBlock, PlacementPage } from 'types';
import { formatBestPrice } from 'utils/betslip';
import { getBetIdentityData } from 'utils/inlinePlacement';

type TInlinePlacementProps = {
  bet: TInlineSelectedBet;
  pageBlock?: PageBlock;
  page?: PlacementPage;
  isPopularMarketsOdds?: boolean;
};

const InlinePlacementItem = ({
  bet,
  isPopularMarketsOdds,
  pageBlock = bet.pageBlock ?? PageBlocks.HOME
}: TInlinePlacementProps) => {
  const dispatch = useDispatch();

  const market = useSelector(getMarketDataById(pageBlock, bet.marketId, bet.sportId));
  const marketStatus = useSelector(getStatusByMarketPricesId(bet.marketId));
  const placementErrorMessage = useSelector(getPlacedSelectedBetErrorMessage(bet.betUuid ?? ''));
  const isDisabledLayOddsForMarket = useSelector(getIsSportLayOddsDisabled(bet.sportId));
  const currentBet = useSelector(getCurrentBetByOfferId(bet?.placedBet?.offerId || 0));
  const marketOddsBackOnly = useSelector(getMarketOddsBackOnly);

  const isClosed = marketStatus === MarketStatuses.CLOSED;
  const isLineMarket = bet.bettingType === BettingTypes.LINE || market?.description?.bettingType === BettingTypes.LINE;
  const isPlacedAction = bet.action === InlinePlacementActions.PLACED;
  const isEditAction = bet.action === InlinePlacementActions.EDIT;
  const isConfirmAction = bet.action === InlinePlacementActions.CONFIRM;
  const isProgressAction =
    bet.action === InlinePlacementActions.PROGRESS || bet.action === InlinePlacementActions.LONG_PROGRESS;
  const isCancelledAction = bet.action === InlinePlacementActions.CANCELLED;

  const isUnavailableBetView = isClosed && !bet.placedBet;
  const isSelectedBetView = !isProgressAction && (!bet.placedBet || isEditAction || isConfirmAction);
  const isPlacedBetView = bet.placedBet && (isPlacedAction || isCancelledAction);

  const betIdData = getBetIdentityData(bet);

  useEffect(() => {
    if (isClosed) {
      dispatch(updateInlineSelectedBet({ ...betIdData, action: InlinePlacementActions.REMOVED }));
    }
  }, [isClosed]);

  useEffect(() => {
    if (bet.placedBet) {
      dispatch(
        updateInlineSelectedBet({
          ...betIdData,
          action: InlinePlacementActions.PLACED,
          price: isLineMarket
            ? formatBestPrice(+(bet.placedBet.handicap ?? 0), bet.marketType, bet.bettingType ?? '')
            : bet.placedBet.price,
          size: bet.placedBet.sizeRemaining,
          profit: bet.placedBet.profit
        })
      );
    }
  }, [bet.placedBet?.offerId]);

  useEffect(() => {
    if (bet.placedBet?.offerState === BetsStatusesTypes.CANCELLED) {
      dispatch(updateInlineSelectedBet({ ...betIdData, action: InlinePlacementActions.CANCELLED }));
    }
  }, [bet.placedBet?.offerState]);

  useEffect(() => {
    if (placementErrorMessage) {
      dispatch(
        updateInlineSelectedBet({
          ...betIdData,
          action: bet.placedBet ? InlinePlacementActions.EDIT : InlinePlacementActions.SELECT,
          placementError: placementErrorMessage
        })
      );
    }
  }, [placementErrorMessage]);

  useEffect(() => {
    if ((isDisabledLayOddsForMarket || (isPopularMarketsOdds && marketOddsBackOnly)) && bet.type === BetSides.Lay) {
      dispatch(removeInlineSelectedBet(bet));
    }
  }, [isDisabledLayOddsForMarket, isPopularMarketsOdds, marketOddsBackOnly]);

  useEffect(() => {
    if (
      bet.placedBet?.offerId &&
      (!currentBet?.offerId || currentBet?.offerState === BetsStatusesTypes.CANCELLED) &&
      bet.action !== InlinePlacementActions.PROGRESS &&
      bet.action !== InlinePlacementActions.LONG_PROGRESS &&
      bet.action !== InlinePlacementActions.CANCELLED
    ) {
      dispatch(removeInlineSelectedBet(bet));
    }
  }, [bet.placedBet?.offerId]);

  useEffect(() => {
    if (!isProgressAction && currentBet?.sizeRemaining && bet.placedBet?.offerId === currentBet?.offerId) {
      dispatch(
        updateInlineSelectedBet({
          ...betIdData,
          ...(!isEditAction ? { size: currentBet?.sizeRemaining } : {}),
          placedBet: currentBet
        })
      );
    }
  }, [currentBet?.sizeRemaining]);

  return (
    <>
      {isUnavailableBetView ? (
        <InlineUnavailableBet bet={bet} />
      ) : (
        <>
          {isPlacedBetView && <InlinePlacedBet bet={bet} />}
          {isSelectedBetView && <InlineSelectedBet bet={bet} />}
          {isProgressAction && <InlineBetProgress bet={bet} />}
        </>
      )}
    </>
  );
};

export default InlinePlacementItem;
